import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Icon } from '@material-ui/core';
import { Card } from '../../elements/ui/card';
import { Button } from '../../elements/ui/button';
import { Skeleton } from '../../elements/ui/skeleton';
import { HealthSystemDomainDeviceResultReaderViewModel } from '../../api/HealthSystemApi';
import { DeviceService } from '../../hooks/DeviceService';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import {
  formatLastSeen,
  READER_HEALTHY_MAX_HOURS,
  READER_STALE_MAX_DAYS,
  ReaderStatus
} from '../../utils/readerStatus';
import { useRevealOnScroll } from '../../hooks/useMotion';
import { ValueFlash } from '../../elements/ui/motion';
import StatusDonut, { DonutSegment } from './StatusDonut';
import { readerStatusColors } from './colors';
import { ReadersSummary, summarizeReaders } from './readersSummary';

const deviceService = new DeviceService();

/** One page is the whole estate's readers - there are tens of these, not thousands. */
const PAGE_SIZE = 500;

const statusLabelKeys: Record<ReaderStatus, string> = {
  healthy: 'f-reader-health-healthy',
  stale: 'f-reader-health-stale',
  down: 'f-reader-health-down'
};

// Updates a DOM node directly every second — no parent re-render, no bounce.
const SecondsAgo: React.FC<{ since: Date | null; label: string }> = ({ since, label }) => {
  const ref = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    if (!since || !ref.current) return;
    const update = () => {
      if (ref.current) {
        const s = Math.floor((Date.now() - since.getTime()) / 1000);
        const display = s >= 120 ? '2m+ ago' : `${s}s ago`;
        ref.current.textContent = `${label} ${display}`;
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [since, label]);

  if (!since) return null;
  return <span ref={ref} className="text-xs text-gray-400 tabular-nums" />;
};

/** The hollow ring that ties a legend line to its arc. Hollow, not filled, so nine of them stay quiet. */
const LegendMarker: React.FC<{ color: string }> = ({ color }) => (
  <span aria-hidden className="h-3.5 w-3.5 shrink-0 rounded-full border-[2.5px]" style={{ borderColor: color }} />
);

/**
 * Status chip in the same form the farm queue's severity chip uses - white pill, palette dot - rather than a
 * tinted `Badge`. Same reason: the colour then means exactly what it means three rows up the page, and a
 * column of these does not stripe the table with three background washes.
 */
const ReaderStatusChip: React.FC<{ status: ReaderStatus }> = ({ status }) => {
  const { t } = useTranslation();

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700">
      <span
        aria-hidden
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: readerStatusColors[status] }}
      />
      {t(statusLabelKeys[status])}
    </span>
  );
};

const PanelHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{children}</p>
);

/** Status donut and its legend: the reader's first question is "is anything wrong". */
const StatusPanel: React.FC<{ summary: ReadersSummary; animate: boolean; viewportRef?: React.Ref<HTMLDivElement> }> = ({
  summary,
  animate,
  viewportRef
}) => {
  const { t } = useTranslation();

  const segments: DonutSegment[] = summary.statuses.map((slice) => ({
    key: slice.status,
    value: slice.count,
    color: slice.color
  }));

  return (
    <div ref={viewportRef} className="flex items-center gap-6 px-6 py-6">
      <StatusDonut
        segments={segments}
        label={t('f-readers-widget-donut-status-label', { percent: summary.healthyPercent })}
        animate={animate}
      >
        <span className="text-xl font-bold text-gray-900 tabular-nums">
          <ValueFlash value={summary.healthyPercent}>{summary.healthyPercent}%</ValueFlash>
        </span>
        <span className="text-[11px] text-gray-500">{t('f-readers-widget-healthy-suffix')}</span>
      </StatusDonut>

      <div className="min-w-0 flex-1">
        <PanelHeading>{t('f-readers-widget-by-status')}</PanelHeading>
        <ul className="space-y-2.5">
          {summary.statuses.map((slice) => (
            <li key={slice.status} className="flex items-center gap-2.5 text-sm">
              <LegendMarker color={slice.color} />
              <span className="min-w-0 flex-1 truncate text-gray-700">{t(statusLabelKeys[slice.status])}</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                <ValueFlash value={slice.count}>{slice.count}</ValueFlash>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/** Slaughterhouse donut and its legend: the second question is "where". */
const SlaughterhousePanel: React.FC<{ summary: ReadersSummary; animate: boolean }> = ({ summary, animate }) => {
  const { t } = useTranslation();

  const { slaughterhouses, otherSlaughterhouses: other } = summary;

  const segments: DonutSegment[] = [
    ...slaughterhouses.map((slice) => ({ key: slice.name, value: slice.total, color: slice.color })),
    ...(other ? [{ key: 'other', value: other.total, color: other.color }] : [])
  ];

  const siteCount = slaughterhouses.length + (other?.siteCount ?? 0);

  return (
    <div className="flex items-center gap-6 px-6 py-6">
      <StatusDonut
        segments={segments}
        label={t('f-readers-widget-donut-sites-label', { count: siteCount })}
        animate={animate}
      >
        <span className="text-xl font-bold text-gray-900 tabular-nums">
          <ValueFlash value={summary.total}>{summary.total}</ValueFlash>
        </span>
        <span className="text-[11px] text-gray-500">{t('f-readers-widget-devices-suffix')}</span>
      </StatusDonut>

      <div className="min-w-0 flex-1">
        <PanelHeading>{t('f-readers-widget-by-slaughterhouse')}</PanelHeading>
        <ul className="space-y-2.5">
          {slaughterhouses.map((slice) => (
            <li key={slice.name} className="flex items-center gap-2.5 text-sm">
              <LegendMarker color={slice.color} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-gray-700">{slice.name}</span>
                {/* Only sites with something wrong get a second line, so the eye lands on them. */}
                {slice.needingAttention > 0 && (
                  <span className="block text-[11px] text-gray-400">
                    {t('f-readers-widget-needing-attention', { count: slice.needingAttention })}
                  </span>
                )}
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                <ValueFlash value={slice.total}>{slice.total}</ValueFlash>
              </span>
            </li>
          ))}

          {/* The pooled tail gets a legend line of its own rather than a footnote: it is a real arc on the
              ring, and its device count has to be somewhere for the totals below to add up. */}
          {other && (
            <li className="flex items-center gap-2.5 text-sm">
              <LegendMarker color={other.color} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-gray-500">
                  {t('f-readers-widget-more-sites', { count: other.siteCount })}
                </span>
                {other.needingAttention > 0 && (
                  <span className="block text-[11px] text-gray-400">
                    {t('f-readers-widget-needing-attention', { count: other.needingAttention })}
                  </span>
                )}
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                <ValueFlash value={other.total}>{other.total}</ValueFlash>
              </span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

const ReaderTable: React.FC<{ summary: ReadersSummary }> = ({ summary }) => {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  const absoluteLastSeen = (lastHeartbeatAt?: string | null) =>
    lastHeartbeatAt ? `${formatDate(lastHeartbeatAt)} ${formatTime(lastHeartbeatAt)}` : t('f-reader-never-seen');

  return (
    <div className="overflow-x-auto">
      {/* `border-collapse` by hand: Tailwind runs without preflight here, so cells default to
          border-spacing and the grey header shows a seam between every column. */}
      <table className="w-full border-collapse text-sm">
        <thead className="border-b border-gray-100 bg-gray-50">
          <tr>
            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">
              {t('f-readers-widget-column-mac')}
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-slaughterhouse')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-com-status')}</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-reader-last-seen')}</th>
            <th className="px-6 py-2 text-right text-xs font-medium text-gray-500">{t('f-reader-firmware')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {summary.rows.map((row) => (
            <tr key={row.reader.id} className="transition-colors hover:bg-gray-50">
              {/* `font-mono!`, not `font-mono`: index.css sets the Roboto face on `*` outside any cascade
                  layer, which outranks every normal utility, so the family only sticks when marked
                  important. A MAC address read out loud off a proportional face is a menace. */}
              <td className="px-6 py-2.5 font-mono! text-xs text-gray-800">{row.reader.macAddress}</td>
              <td className="px-4 py-2.5">
                <span className="flex items-center gap-2 text-gray-700">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ backgroundColor: row.slaughterhouseColor }}
                  />
                  {row.slaughterhouseName}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <ReaderStatusChip status={row.status} />
              </td>
              {/* The relative age is what gets scanned; the exact stamp is one hover away, in farm time. */}
              <td className="px-4 py-2.5 text-xs text-gray-500" title={absoluteLastSeen(row.reader.lastHeartbeatAt)}>
                {formatLastSeen(row.reader.lastHeartbeatAt)}
              </td>
              <td className="px-6 py-2.5 text-right font-mono! text-xs text-gray-400">
                {row.reader.lastFirmwareVersion ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Estate-wide slaughterhouse reader health, the last card on the admin farms overview.
 *
 * Two donuts and their legends are the whole card at rest: how many devices are reporting, and which
 * slaughterhouses the quiet ones belong to. That is the answer an admin needs on the way past, and it holds its
 * height whether the estate runs four readers or four hundred - which is why the per-device list is folded
 * away behind "view all" rather than printed under the chart. Opening it is a local disclosure, not a
 * navigation: the counts stay on screen above the rows they explain.
 *
 * Devices are judged by heartbeat age (see `getReaderStatusByAge`), not by farm - readers belong to
 * slaughterhouses - so this card is estate-wide even though the page around it is a per-farm queue.
 */
const ReadersStatusWidget: React.FC = () => {
  const { t } = useTranslation();
  const [readers, setReaders] = React.useState<HealthSystemDomainDeviceResultReaderViewModel[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [lastRefreshed, setLastRefreshed] = React.useState<Date | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  // The status panel is the trigger: it is what the donuts sit in, and it only mounts once there is data.
  const { ref: statusPanelRef, revealed } = useRevealOnScroll();
  const summary = React.useMemo(() => summarizeReaders(readers), [readers]);

  const loadReaders = React.useCallback(async () => {
    setBusy(true);
    try {
      const result = await deviceService.getReaders(0, PAGE_SIZE);
      setReaders(result.data);
      setLastRefreshed(new Date());
    } catch {
      // silent — widget is non-critical
    } finally {
      setBusy(false);
      setInitialLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadReaders();
  }, [loadReaders]);

  const renderBody = () => {
    if (initialLoading) return <WidgetSkeleton />;

    if (summary.total === 0) {
      return (
        <div className="border-t border-gray-200 px-6 py-10 text-center text-sm text-gray-500">
          {t('f-readers-widget-empty')}
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 border-t border-gray-200 md:grid-cols-2 md:divide-x md:divide-gray-200">
          <StatusPanel summary={summary} animate={revealed} viewportRef={statusPanelRef} />
          <SlaughterhousePanel summary={summary} animate={revealed} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-3">
          <p className="text-sm text-gray-600">
            {t('f-readers-widget-all-devices', { count: summary.total })}
            {summary.needingAttention > 0 && (
              <>
                {' · '}
                <span className="text-gray-700">
                  {t('f-readers-widget-needing-attention', { count: summary.needingAttention })}
                </span>
              </>
            )}
          </p>

          <Button variant="outline" size="sm" onClick={() => setExpanded((open) => !open)} aria-expanded={expanded}>
            {t(expanded ? 'f-readers-widget-hide-all' : 'f-readers-widget-view-all')}
          </Button>
        </div>

        {expanded && <ReaderTable summary={summary} />}
      </>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{t('f-readers-widget-title')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {t('f-readers-widget-subtitle', {
              hours: READER_HEALTHY_MAX_HOURS,
              days: READER_STALE_MAX_DAYS
            })}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <SecondsAgo since={lastRefreshed} label={t('f-reader-last-updated')} />

          {/* Tailwind runs without preflight here, so the native button chrome is reset by hand. */}
          <button
            type="button"
            onClick={loadReaders}
            disabled={busy}
            aria-label={t('f-reload')}
            className="flex h-9 w-9 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border border-indigo-200 bg-white p-0 text-indigo-600 transition-colors hover:bg-indigo-50 disabled:cursor-default disabled:opacity-60"
          >
            {busy && !initialLoading ? (
              <CircularProgress size={14} thickness={4} />
            ) : (
              <Icon style={{ fontSize: 18 }}>refresh</Icon>
            )}
          </button>
        </div>
      </div>

      {renderBody()}
    </Card>
  );
};

const DonutSkeleton = () => (
  <div className="flex items-center gap-6 px-6 py-6">
    <Skeleton className="h-[132px] w-[132px] shrink-0 rounded-full" />
    <div className="min-w-0 flex-1 space-y-3">
      <Skeleton className="h-3 w-24" />
      {(['w-20', 'w-16', 'w-24'] as const).map((width) => (
        <Skeleton key={width} className={`h-3.5 ${width}`} />
      ))}
    </div>
  </div>
);

const WidgetSkeleton: React.FC = () => (
  <>
    <div className="grid grid-cols-1 border-t border-gray-200 md:grid-cols-2 md:divide-x md:divide-gray-200">
      <DonutSkeleton />
      <DonutSkeleton />
    </div>
    <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3">
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-8 w-20 rounded-md" />
    </div>
  </>
);

export default ReadersStatusWidget;
